// src/components/superuser/UserEditDialog.js
//
// Edit an existing account or a pending invite from the superuser Users page.
// Users: change role, assigned properties, and active/suspended state.
// Invites: change role and assigned properties before acceptance.
// Property assignments are always stamped with the client's id.

import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { deleteDoc, doc, updateDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { firestore } from "../../firebase";

// Roles a superuser can assign (superuser itself is intentionally excluded).
export const ASSIGNABLE_ROLES = [
  { value: "manager", label: "Property Manager" },
  { value: "facilities", label: "Facilities" },
  { value: "maintenance", label: "Maintenance" },
  { value: "housekeeping", label: "Housekeeping" },
  { value: "frontDesk", label: "Front Desk" },
];

export default function UserEditDialog({
  open,
  onClose,
  row,
  clientId,
  clientProperties = [],
  onSaved,
}) {
  const isInvite = row?.kind === "invite";
  const [userType, setUserType] = useState("");
  const [propertyIds, setPropertyIds] = useState([]);
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    if (!row) return;
    setUserType(row.userType || "");
    setPropertyIds((row.properties || []).map((p) => p.id));
    setActive(row.status !== "suspended");
    setError("");
  }, [row]);

  const nameById = useMemo(() => {
    const m = {};
    clientProperties.forEach((p) => (m[p.id] = p.displayName || p.id));
    (row?.properties || []).forEach((p) => {
      if (!m[p.id]) m[p.id] = p.displayName || p.name || p.id;
    });
    return m;
  }, [clientProperties, row]);

  // Selected option objects for the Autocomplete, preserving any assigned
  // property that isn't in the current client list (edge case).
  const selectedProperties = useMemo(
    () =>
      propertyIds.map(
        (id) =>
          clientProperties.find((p) => p.id === id) || {
            id,
            displayName: nameById[id] || id,
          }
      ),
    [propertyIds, clientProperties, nameById]
  );

  const buildProperties = () =>
    propertyIds.map((id) => ({
      id,
      displayName: nameById[id] || id,
      clientId,
    }));

  const handleSave = async () => {
    if (!row) return;
    if (!userType) {
      setError("Please choose a role.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (isInvite) {
        await updateDoc(doc(firestore, "userInvites", row.id), {
          userType,
          properties: buildProperties(),
        });
      } else {
        await updateDoc(doc(firestore, "users", row.id), {
          userType,
          properties: buildProperties(),
        });
        // Suspending must actually disable the Firebase Auth account (a
        // Firestore flag alone doesn't block sign-in), so route it through the
        // admin function — but only when the active state actually changed.
        const wasActive = row.status !== "suspended";
        if (active !== wasActive) {
          const fn = httpsCallable(getFunctions(), "setUserSuspended");
          await fn({ userId: row.id, suspended: !active });
        }
      }
      onSaved?.({
        severity: "success",
        message: `${row.name} updated`,
      });
      onClose?.();
    } catch (err) {
      console.error("save user failed", err);
      setError(err?.message || "Could not save changes.");
    }
    setSaving(false);
  };

  const handleRemove = async () => {
    if (!row) return;
    setRemoving(true);
    setError("");
    try {
      if (isInvite) {
        await deleteDoc(doc(firestore, "userInvites", row.id));
      } else {
        const fn = httpsCallable(getFunctions(), "deleteUserAccount");
        await fn({ userId: row.id });
      }
      onSaved?.({
        severity: "info",
        message: `${row.name} ${isInvite ? "invitation deleted" : "removed"}`,
      });
      setConfirmRemove(false);
      onClose?.();
    } catch (err) {
      console.error("remove user failed", err);
      setError(err?.message || "Could not remove.");
      setConfirmRemove(false);
    }
    setRemoving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isInvite ? "Edit Invitation" : "Edit User"}
        <Typography variant="body2" color="text.secondary">
          {row?.name} · {row?.email}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ mt: 0.5 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              label="Role"
              value={userType}
              onChange={(e) => setUserType(e.target.value)}
            >
              {ASSIGNABLE_ROLES.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  {r.label}
                </MenuItem>
              ))}
              {/* Preserve an existing non-standard role (e.g. superuser). */}
              {userType &&
                !ASSIGNABLE_ROLES.some((r) => r.value === userType) && (
                  <MenuItem value={userType}>{userType}</MenuItem>
                )}
            </Select>
          </FormControl>

          <Autocomplete
            multiple
            options={clientProperties}
            value={selectedProperties}
            onChange={(e, val) => setPropertyIds(val.map((v) => v.id))}
            getOptionLabel={(o) => o.displayName || o.id}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            filterSelectedOptions
            noOptionsText="No properties found for this client"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Assigned Properties"
                placeholder="Search properties…"
              />
            )}
          />

          {!isInvite && (
            <FormControlLabel
              control={
                <Switch
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                  color="success"
                />
              }
              label={active ? "Account active" : "Account suspended"}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          color="error"
          startIcon={<DeleteOutlineIcon />}
          onClick={() => setConfirmRemove(true)}
          disabled={saving}
        >
          {isInvite ? "Delete Invite" : "Remove"}
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save Changes"}
        </Button>
      </DialogActions>

      {/* Remove confirmation */}
      <Dialog open={confirmRemove} onClose={() => setConfirmRemove(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{isInvite ? "Delete invitation?" : "Remove user?"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {isInvite ? (
              <>
                This permanently deletes the invitation for{" "}
                <strong>{row?.email}</strong>. They will no longer be able to
                register with the current link.
              </>
            ) : (
              <>
                This permanently removes <strong>{row?.name}</strong>'s account
                and sign-in access. This cannot be undone.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setConfirmRemove(false)} disabled={removing}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRemove}
            disabled={removing}
          >
            {removing ? "Removing…" : isInvite ? "Delete Invite" : "Remove User"}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
